import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { Upload, Edit, Trash2, Eye, X, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import {
  uploadImage,
  deleteImage,
  replaceReadyMadeAssets,
  getReadyMadeDesignsPage,
  normalizePaginatedItems,
  ADMIN_DESIGNS_PAGE_SIZE,
} from '../apiClient';

const mapRowToDesign = (d) => ({
  id: `api-${d.id}`,
  dbId: d.id,
  name: d.name || d.prompt || 'Design',
  imageUrl: d.mockup_image_url || d.image_url,
  artUrl: d.image_url,
  mockupUrl: d.mockup_image_url || null,
  category: (d.category || d.style) && (d.category || d.style) !== 'User Upload' ? (d.category || d.style) : 'Preset',
  price: d.price || '$29.99',
  status: 'Active',
});

const AdminDesignManager = () => {
  const [apiDesigns, setApiDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageOffset, setPageOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const printInputRef = useRef(null);
  const mockupInputRef = useRef(null);
  const [printFile, setPrintFile] = useState(null);
  const [mockupFile, setMockupFile] = useState(null);
  const [designTitle, setDesignTitle] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editingDesign, setEditingDesign] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editPrintFile, setEditPrintFile] = useState(null);
  const [editMockupFile, setEditMockupFile] = useState(null);
  const [editClearMockup, setEditClearMockup] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const editPrintInputRef = useRef(null);
  const editMockupInputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const loadApiDesigns = useCallback((offset = pageOffset, q = debouncedSearch) => {
    setLoading(true);
    const term = (q || '').trim();
    const params = { limit: ADMIN_DESIGNS_PAGE_SIZE, offset };
    if (term) params.q = term;
    getReadyMadeDesignsPage(params)
      .then((data) => {
        const items = normalizePaginatedItems(data);
        setApiDesigns(items.map(mapRowToDesign));
        setTotalCount(typeof data?.total === 'number' ? data.total : items.length);
      })
      .catch(() => {
        setApiDesigns([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [pageOffset, debouncedSearch]);

  useEffect(() => {
    loadApiDesigns(pageOffset);
  }, [pageOffset, loadApiDesigns]);

  const pageCount = Math.max(1, Math.ceil(totalCount / ADMIN_DESIGNS_PAGE_SIZE));
  const currentPage = Math.floor(pageOffset / ADMIN_DESIGNS_PAGE_SIZE) + 1;
  const canGoPrev = pageOffset > 0;
  const canGoNext = pageOffset + ADMIN_DESIGNS_PAGE_SIZE < totalCount;

  const goToPage = (nextOffset) => {
    const clamped = Math.max(0, nextOffset);
    setPageOffset(clamped);
  };

  const resetModal = () => {
    setPrintFile(null);
    setMockupFile(null);
    setDesignTitle('');
    if (printInputRef.current) printInputRef.current.value = '';
    if (mockupInputRef.current) mockupInputRef.current.value = '';
  };

  const closeUpload = () => {
    setUploadOpen(false);
    setUploadError(null);
    resetModal();
  };

  const resetEditModal = () => {
    setEditingDesign(null);
    setEditPrintFile(null);
    setEditMockupFile(null);
    setEditClearMockup(false);
    setEditTitle('');
    setEditError(null);
    if (editPrintInputRef.current) editPrintInputRef.current.value = '';
    if (editMockupInputRef.current) editMockupInputRef.current.value = '';
  };

  const closeEdit = () => {
    setEditOpen(false);
    resetEditModal();
  };

  const openEdit = (design) => {
    if (design.dbId == null) return;
    setUploadError(null);
    setDeleteError(null);
    setEditError(null);
    setEditingDesign(design);
    setEditPrintFile(null);
    setEditMockupFile(null);
    setEditClearMockup(false);
    setEditTitle(design.name || '');
    setEditOpen(true);
    requestAnimationFrame(() => {
      if (editPrintInputRef.current) editPrintInputRef.current.value = '';
      if (editMockupInputRef.current) editMockupInputRef.current.value = '';
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingDesign?.dbId) return;
    const nameChanged = (editTitle || '').trim() !== (editingDesign.name || '').trim();
    if (nameChanged && !(editTitle || '').trim()) {
      setEditError('Display name cannot be empty.');
      return;
    }
    if (!editPrintFile && !editMockupFile && !editClearMockup && !nameChanged) {
      setEditError('Update at least one: print file, mockup, remove mockup, or display name.');
      return;
    }
    if (editMockupFile && editClearMockup) {
      setEditError('Either upload a new mockup or remove it — not both.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await replaceReadyMadeAssets(editingDesign.dbId, {
        printFile: editPrintFile || undefined,
        mockupFile: editMockupFile || undefined,
        clearMockup: editClearMockup || undefined,
        description: nameChanged ? (editTitle || '').trim() : undefined,
      });
      loadApiDesigns(pageOffset);
      closeEdit();
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Update failed.';
      setEditError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setEditSaving(false);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!printFile) {
      setUploadError('Select the print-ready artwork file first.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      await uploadImage(printFile, {
        readyMade: true,
        mockupFile: mockupFile || undefined,
        description: designTitle.trim() || undefined,
      });
      setPageOffset(0);
      closeUpload();
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Upload failed.';
      setUploadError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (design) => {
    if (design.dbId == null) return;
    setDeleteError(null);
    if (!window.confirm(`Delete "${design.name}" from Ready-Made assets? This cannot be undone.`)) return;
    try {
      await deleteImage(design.dbId);
      const nextTotal = Math.max(0, totalCount - 1);
      const nextOffset =
        pageOffset >= nextTotal && pageOffset > 0
          ? Math.max(0, pageOffset - ADMIN_DESIGNS_PAGE_SIZE)
          : pageOffset;
      if (nextOffset !== pageOffset) {
        setPageOffset(nextOffset);
      } else {
        loadApiDesigns(pageOffset);
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Delete failed.';
      setDeleteError(typeof message === 'string' ? message : JSON.stringify(message));
    }
  };

  const designs = apiDesigns;

  return (
    <SessionAuth>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Design Assets</h1>
            <p className="text-gray-600 mt-1">
              All curated designs live in <strong>MinIO</strong> and the database. Use Upload to add rows, Edit to replace
              print or mockup (or rename), Delete to remove assets from storage.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={() => {
                setUploadError(null);
                setDeleteError(null);
                setUploadOpen(true);
              }}
              disabled={uploading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={20} />
              Upload New Design
            </button>
            {uploadError && !uploadOpen && (
              <p className="text-sm text-red-600" role="alert">
                {uploadError}
              </p>
            )}
            {deleteError && (
              <p className="text-sm text-red-600" role="alert">
                {deleteError}
              </p>
            )}
          </div>
        </div>

        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="relative flex-1 max-w-md">
            <span className="sr-only">Search by name or description</span>
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              aria-hidden
            />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPageOffset(0);
              }}
              placeholder="Search by name or description…"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </label>
          {searchQuery.trim() && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setPageOffset(0);
              }}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Clear search
            </button>
          )}
        </div>

        {loading && <p className="text-center text-gray-500 py-4">Loading assets…</p>}
        {!loading && totalCount === 0 && searchQuery.trim() && (
          <p className="text-center text-gray-500 py-4">No designs match &quot;{searchQuery.trim()}&quot;.</p>
        )}
        {!loading && totalCount > 0 && (
          <p className="text-sm text-gray-600 mb-3">
            Showing {pageOffset + 1}–{Math.min(pageOffset + ADMIN_DESIGNS_PAGE_SIZE, totalCount)} of {totalCount}{' '}
            design{totalCount === 1 ? '' : 's'}
            {searchQuery.trim() ? ` matching “${searchQuery.trim()}”` : ''}
          </p>
        )}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Print artwork
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Mockup
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {designs.map((design) => {
                const printSrc = design.artUrl || design.imageUrl;
                const mockSrc = design.mockupUrl || design.imageUrl;
                return (
                  <tr key={design.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-20 w-20 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                        <img className="h-full w-full object-contain" src={printSrc} alt="" title="Print file" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-20 w-20 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                        <img className="h-full w-full object-contain" src={mockSrc} alt="" title="Storefront preview" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">{design.name}</div>
                      <div className="text-sm text-gray-500">{design.price}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {design.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {design.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-3">
                        <a
                          href={printSrc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-600 transition-colors inline-flex"
                          title="View print artwork"
                        >
                          <Eye size={18} />
                        </a>
                        <button
                          type="button"
                          onClick={() => openEdit(design)}
                          disabled={design.dbId == null}
                          className="text-indigo-400 hover:text-indigo-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title={design.dbId != null ? 'Replace print/mockup or rename (MinIO)' : 'No database id — cannot edit'}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(design)}
                          disabled={design.dbId == null}
                          className="text-red-400 hover:text-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title={design.dbId != null ? 'Delete from Ready-Made (removes MinIO objects)' : 'Cannot delete'}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!loading && totalCount > ADMIN_DESIGNS_PAGE_SIZE && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              Page {currentPage} of {pageCount}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => goToPage(pageOffset - ADMIN_DESIGNS_PAGE_SIZE)}
                disabled={!canGoPrev}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft size={18} />
                Previous
              </button>
              <button
                type="button"
                onClick={() => goToPage(pageOffset + ADMIN_DESIGNS_PAGE_SIZE)}
                disabled={!canGoNext}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 p-4 bg-sky-50 border border-sky-200 rounded-lg flex items-start gap-3">
          <div className="text-sky-600 mt-1">
            <Upload size={20} />
          </div>
          <div>
            <h4 className="text-sky-900 font-semibold">Two images for each Ready-Made design</h4>
            <p className="text-sm text-sky-800 mt-1">
              Use <strong>Upload</strong> or <strong>Edit</strong> to manage print + optional mockup in MinIO. Customers
              still configure the real product mockup at order time; the gallery mockup is for Ready-Made cards only.
            </p>
          </div>
        </div>

        {uploadOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-900">Upload Ready-Made design</h2>
                <button type="button" onClick={closeUpload} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleUploadSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-1">Print-ready artwork *</label>
                  <input
                    ref={printInputRef}
                    type="file"
                    accept="image/*"
                    className="block w-full text-sm"
                    onChange={(ev) => setPrintFile(ev.target.files?.[0] || null)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-1">Mockup on shirt (optional)</label>
                  <input
                    ref={mockupInputRef}
                    type="file"
                    accept="image/*"
                    className="block w-full text-sm"
                    onChange={(ev) => setMockupFile(ev.target.files?.[0] || null)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-1">Display name</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. Astronaut"
                    value={designTitle}
                    onChange={(ev) => setDesignTitle(ev.target.value)}
                  />
                </div>
                {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={closeUpload} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-50"
                  >
                    {uploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}

        {editOpen && editingDesign ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-900">Edit Ready-Made: {editingDesign.name}</h2>
                <button type="button" onClick={closeEdit} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="p-4 space-y-4">
                <p className="text-sm text-gray-600">
                  Leave a file field empty to keep the current file. You can change the print, the mockup, or both.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-1">New print-ready artwork (optional)</label>
                  <input
                    ref={editPrintInputRef}
                    type="file"
                    accept="image/*"
                    className="block w-full text-sm"
                    onChange={(ev) => setEditPrintFile(ev.target.files?.[0] || null)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-1">New mockup on shirt (optional)</label>
                  <input
                    ref={editMockupInputRef}
                    type="file"
                    accept="image/*"
                    className="block w-full text-sm"
                    onChange={(ev) => {
                      const f = ev.target.files?.[0] || null;
                      setEditMockupFile(f);
                      if (f) setEditClearMockup(false);
                    }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="edit-clear-mockup"
                    type="checkbox"
                    checked={editClearMockup}
                    onChange={(ev) => {
                      setEditClearMockup(ev.target.checked);
                      if (ev.target.checked) {
                        setEditMockupFile(null);
                        if (editMockupInputRef.current) editMockupInputRef.current.value = '';
                      }
                    }}
                  />
                  <label htmlFor="edit-clear-mockup" className="text-sm text-gray-800">
                    Remove mockup only (storefront will fall back to print artwork preview)
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-1">Display name</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={editTitle}
                    onChange={(ev) => setEditTitle(ev.target.value)}
                  />
                </div>
                {editError && <p className="text-sm text-red-600">{editError}</p>}
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={closeEdit} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editSaving}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium disabled:opacity-50"
                  >
                    {editSaving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </SessionAuth>
  );
};

export default AdminDesignManager;
